// Change overs: 1–5 second 3D stings between two shots — made from a prompt
// (and reference pictures / videos), edited in a full-screen studio.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FiShuffle, FiPlus, FiTrash2, FiRefreshCw, FiImage, FiX, FiFilm, FiSearch, FiStar, FiClock, FiCopy, FiUploadCloud, FiEdit2, FiCheck,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { changeoverService } from '../services/apiService';
import { useT } from '../utils/i18n';
import { showError } from './FullStory/shared';
import { ChangeoverStudio } from './ChangeoverStudio';
import { drawChangeover, useDrawState, canDraw } from './changeoverEngine';

const MAX_FILES = 5;
export const isBusy = (c) => ['designing', 'drawing', 'packaging'].includes(c?.status);
/** The current version's files are made. */
export const hasMedia = (c) => Boolean(c?.media?.mp4);
export const mediaVersion = (c) => `${c?.media?.version || 0}.${c?.rendered || 0}`;

const Bar = ({ pct }) => <div className="mt-1 h-1 rounded bg-gray-100 overflow-hidden"><div className="h-full bg-primary-500 transition-all" style={{ width: `${pct}%` }} /></div>;

export const StatusLine = ({ item }) => {
  const t = useT();
  const local = useDrawState(item.id);
  if (local?.phase === 'error') return <p className="text-xs text-red-600">{local.error}</p>;
  if (local && local.phase !== 'waiting') {
    const pct = Math.round((local.p || 0) * 100);
    return <div><p className="text-xs text-primary-600">{t(`changeovers.phase.${local.phase}`, { pct })}</p><Bar pct={local.phase === 'packaging' ? 100 : pct} /></div>;
  }
  if (item.status === 'designing') return <p className="text-xs text-primary-600 flex items-center gap-1.5"><FiRefreshCw size={11} className="animate-spin" />{t('changeovers.designing')}</p>;
  if (item.status === 'packaging') return <p className="text-xs text-primary-600 flex items-center gap-1.5"><FiRefreshCw size={11} className="animate-spin" />{t('changeovers.phase.packaging')}</p>;
  if (item.status === 'drawing') {
    return <p className="text-xs text-primary-600 flex items-center gap-1.5"><FiRefreshCw size={11} className="animate-spin" />{item.drawingBy ? t('changeovers.phase.drawingBy', { name: item.drawingBy }) : t('changeovers.phase.waiting')}</p>;
  }
  if (item.status === 'failed') return <p className="text-xs text-red-600">{item.error || t('changeovers.failed')}</p>;
  return null;
};

/** Favourite (shared, labelled) and recent prompts: pick one to use. */
export const PromptPicker = ({ onUse, onClose }) => {
  const t = useT();
  const [tab, setTab] = useState('favorites');
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => changeoverService.prompts().then(setData).catch(showError), []);
  useEffect(() => { load(); }, [load]);
  const list = data ? data[tab === 'favorites' ? 'favorites' : 'recent'] : [];
  const relabel = async (fav) => {
    try {
      await changeoverService.relabelFavorite(fav.id, { label: editing.label });
      setEditing(null);
      load();
    } catch (e) { showError(e); }
  };
  const remove = async (fav) => {
    if (!window.confirm(t('changeovers.fav.removeConfirm', { label: fav.label }))) return;
    try { await changeoverService.removeFavorite(fav.id); load(); } catch (e) { showError(e); }
  };
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-2">
        <div className="flex">
          {[['favorites', FiStar, t('changeovers.fav.favorites')], ['recent', FiClock, t('changeovers.fav.recent')]].map(([id, Icon, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${tab === id ? 'border-primary-500 text-primary-700 font-medium' : 'border-transparent text-gray-500'}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-400"><FiX size={15} /></button>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
        {!data && <p className="p-4 text-sm text-gray-400">{t('changeovers.loading')}</p>}
        {data && !list.length && <p className="p-4 text-sm text-gray-400">{tab === 'favorites' ? t('changeovers.fav.none') : t('changeovers.fav.noRecent')}</p>}
        {list.map((p) => (
          <div key={p.id || p.prompt} className="p-3 hover:bg-gray-50 group">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                {tab === 'favorites' && editing?.id === p.id ? (
                  <div className="flex gap-1.5 mb-1">
                    <input autoFocus value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && relabel(p)} className="input text-sm py-1 flex-1" />
                    <button type="button" onClick={() => relabel(p)} className="p-1.5 rounded bg-primary-50 text-primary-600"><FiCheck size={13} /></button>
                  </div>
                ) : (
                  <p className="text-sm font-medium text-gray-800 truncate">{tab === 'favorites' ? p.label : p.name}</p>
                )}
                <p className="text-xs text-gray-500 line-clamp-2">{p.prompt}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {tab === 'favorites' && (
                  <>
                    <button type="button" title={t('changeovers.fav.rename')} onClick={() => setEditing({ id: p.id, label: p.label })} className="p-1.5 rounded text-gray-400 hover:bg-gray-100 opacity-0 group-hover:opacity-100"><FiEdit2 size={13} /></button>
                    <button type="button" title={t('changeovers.remove')} onClick={() => remove(p)} className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 opacity-0 group-hover:opacity-100"><FiTrash2 size={13} /></button>
                  </>
                )}
                <button type="button" onClick={() => onUse(p.prompt)} className="btn btn-secondary text-xs py-1 px-2.5">{t('changeovers.fav.use')}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Save a prompt as a favourite, with a label. */
export const FavoriteButton = ({ prompt, className = '' }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const save = async () => {
    try {
      await changeoverService.addFavorite(label, prompt);
      toast.success(t('changeovers.fav.saved'));
      setOpen(false);
      setLabel('');
    } catch (e) { showError(e); }
  };
  if (!open) {
    return (
      <button type="button" disabled={!prompt?.trim()} onClick={() => setOpen(true)} className={`btn btn-secondary text-xs flex items-center gap-1.5 py-1.5 ${className}`}>
        <FiStar size={13} /> {t('changeovers.fav.add')}
      </button>
    );
  }
  return (
    <span className={`flex items-center gap-1.5 ${className}`}>
      <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} placeholder={t('changeovers.fav.label')} className="input text-xs py-1.5 w-44" />
      <button type="button" onClick={save} className="btn btn-primary text-xs py-1.5">{t('changeovers.fav.save')}</button>
      <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded text-gray-400 hover:bg-gray-100"><FiX size={13} /></button>
    </span>
  );
};

export const TagInput = ({ tags, onChange, placeholder }) => {
  const [value, setValue] = useState('');
  const add = () => {
    const v = value.trim();
    if (v && !tags.includes(v)) onChange([...tags, v].slice(0, 12));
    setValue('');
  };
  return (
    <div className="input flex flex-wrap items-center gap-1.5 py-1.5 min-h-[38px]">
      {tags.map((tag) => (
        <span key={tag} className="flex items-center gap-1 text-xs bg-primary-50 text-primary-700 rounded-full pl-2 pr-1 py-0.5">
          {tag}
          <button type="button" onClick={() => onChange(tags.filter((x) => x !== tag))} className="rounded-full hover:bg-primary-100 p-0.5"><FiX size={10} /></button>
        </span>
      ))}
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } else if (e.key === 'Backspace' && !value && tags.length) onChange(tags.slice(0, -1)); }}
        onBlur={add}
        placeholder={tags.length ? '' : placeholder}
        className="flex-1 min-w-[100px] text-sm outline-none bg-transparent"
      />
    </div>
  );
};

/** A reference file; for a video, the part to follow (from–to seconds), previewed. */
const FileChip = ({ file, range, onRange, onRemove }) => {
  const t = useT();
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const video = file.type.startsWith('video/');
  const ref = useRef(null);
  const [duration, setDuration] = useState(0);
  const seek = (s) => { if (ref.current) ref.current.currentTime = s; };
  const num = (v) => Math.max(0, Math.min(duration || 9999, Number(v) || 0));
  return (
    <div className={`relative rounded-lg overflow-hidden border border-gray-200 bg-gray-900 ${video ? 'w-56' : 'w-28'}`}>
      {video
        ? <video ref={ref} src={url} muted controls onLoadedMetadata={(e) => { setDuration(e.target.duration || 0); if (!range) onRange({ from: 0, to: Math.min(e.target.duration || 0, 12) }); }} className="w-full aspect-video object-cover" />
        : <img src={url} alt="" className="w-full aspect-video object-cover" />}
      <button type="button" onClick={onRemove} className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white"><FiX size={12} /></button>
      <p className="text-[10px] text-gray-200 truncate px-1.5 py-0.5 bg-gray-800 flex items-center gap-1">{video ? <FiFilm size={9} /> : <FiImage size={9} />}{file.name} · {(file.size / 1048576).toFixed(1)} MB</p>
      {video && range && (
        <div className="bg-gray-800 px-1.5 pb-1.5 text-[10px] text-gray-300 flex items-center gap-1">
          {t('changeovers.create.part')}
          <input type="number" step="0.1" min="0" value={range.from} onChange={(e) => { const v = num(e.target.value); onRange({ ...range, from: v }); seek(v); }} className="w-12 rounded bg-gray-700 text-white px-1 py-0.5" />
          –
          <input type="number" step="0.1" min="0" value={range.to} onChange={(e) => { const v = num(e.target.value); onRange({ ...range, to: v }); seek(v); }} className="w-12 rounded bg-gray-700 text-white px-1 py-0.5" />
          s
          <button type="button" title={t('changeovers.create.useNow')} onClick={() => { const v = Number((ref.current?.currentTime || 0).toFixed(1)); onRange(v < range.to ? { ...range, from: v } : { ...range, to: v }); }} className="ml-auto rounded bg-gray-700 px-1.5 py-0.5 hover:bg-gray-600">{t('changeovers.create.useNow')}</button>
        </div>
      )}
    </div>
  );
};

/** Create: prompt (or a favourite / recent one), name, tags and up to 5 reference pictures or videos. */
export const CreateDialog = ({ initialPrompt = '', onClose, onCreated }) => {
  const t = useT();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [name, setName] = useState('');
  const [tags, setTags] = useState([]);
  const [files, setFiles] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [picker, setPicker] = useState(false);
  const [sending, setSending] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);
  const addFiles = (list) => {
    const ok = Array.from(list || []).filter((f) => /^(image\/(jpeg|png|webp)|video\/)/.test(f.type));
    if (ok.length < (list?.length || 0)) toast.warn(t('changeovers.create.badFile'));
    setFiles((cur) => [...cur, ...ok].slice(0, MAX_FILES));
  };
  const removeFile = (i) => { setFiles((cur) => cur.filter((_, j) => j !== i)); setRanges((cur) => cur.filter((_, j) => j !== i)); };
  const create = async () => {
    setSending(0);
    try {
      const made = await changeoverService.create({ prompt: prompt.trim(), name: name.trim(), tags, files, ranges: files.map((_, i) => ranges[i] || null) }, (e) => setSending(e.total ? e.loaded / e.total : 0));
      onCreated(made);
    } catch (e) {
      showError(e);
      setSending(null);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <p className="font-semibold text-gray-900 flex items-center gap-2"><FiShuffle className="text-primary-600" /> {t('changeovers.create.title')}</p>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><FiX size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="co-prompt">{t('changeovers.promptLabel')}</label>
              <button type="button" onClick={() => setPicker((v) => !v)} className="text-xs text-primary-600 hover:underline flex items-center gap-1"><FiStar size={12} /> {t('changeovers.fav.pick')}</button>
            </div>
            {picker && <div className="mb-2"><PromptPicker onUse={(p) => { setPrompt(p); setPicker(false); }} onClose={() => setPicker(false)} /></div>}
            <textarea id="co-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} maxLength={2000} placeholder={t('changeovers.promptPlaceholder')} className="input w-full text-sm" />
            <div className="flex justify-between items-center mt-1.5">
              <FavoriteButton prompt={prompt} />
              <span className="text-[11px] text-gray-400">{prompt.length}/2000</span>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1.5">{t('changeovers.create.references')} <span className="font-normal text-gray-400">· {t('changeovers.create.optional')}</span></p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
              className={`rounded-xl border-2 border-dashed p-3 transition-colors ${drag ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}`}
            >
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <FileChip key={`${f.name}-${f.size}-${f.lastModified}`} file={f} range={ranges[i]} onRange={(r) => setRanges((cur) => { const n = [...cur]; n[i] = r; return n; })} onRemove={() => removeFile(i)} />
                ))}
                {files.length < MAX_FILES && (
                  <button type="button" onClick={() => input.current?.click()} className="w-28 aspect-video rounded-lg border border-gray-200 text-gray-400 hover:text-primary-600 hover:border-primary-300 flex flex-col items-center justify-center gap-1 text-[11px]">
                    <FiUploadCloud size={18} /> {t('changeovers.create.addFiles')}
                  </button>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">{t('changeovers.create.filesHint')}</p>
            </div>
            <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,video/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5" htmlFor="co-name">{t('changeovers.details.name')} <span className="font-normal text-gray-400">· {t('changeovers.create.optional')}</span></label>
              <input id="co-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t('changeovers.create.namePlaceholder')} className="input w-full text-sm" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1.5">{t('changeovers.details.tags')}</p>
              <TagInput tags={tags} onChange={setTags} placeholder={t('changeovers.details.tagsPlaceholder')} />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <span className="text-xs text-gray-400">{t('changeovers.costHint')}</span>
          <div className="flex items-center gap-2">
            {sending !== null && files.length > 0 && <span className="text-xs text-gray-500">{t('changeovers.create.uploading', { pct: Math.round(sending * 100) })}</span>}
            <button type="button" onClick={onClose} className="btn btn-secondary">{t('changeovers.cancel')}</button>
            <button type="button" onClick={create} disabled={sending !== null || !prompt.trim()} className="btn btn-primary flex items-center gap-2">
              <FiPlus size={15} /> {sending !== null ? t('changeovers.creating') : t('changeovers.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Card = ({ item, onOpen, onRemove, onRetry, onDuplicate }) => {
  const t = useT();
  const ready = hasMedia(item);
  const [hover, setHover] = useState(false);
  return (
    <div className="card p-0 overflow-hidden group/card">
      <button type="button" onClick={() => onOpen(item)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="relative block w-full aspect-video bg-gray-900">
        {ready ? (
          hover
            ? <video src={changeoverService.fileUrl(item.id, 'video', mediaVersion(item))} autoPlay muted loop playsInline className="w-full h-full object-cover" />
            : <img src={changeoverService.fileUrl(item.id, 'poster', mediaVersion(item))} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-gray-500">
            {isBusy(item) ? <FiRefreshCw className="animate-spin" size={22} /> : <FiShuffle size={22} />}
          </span>
        )}
        {item.spec && <span className="absolute right-2 bottom-2 text-[10px] bg-black/60 text-white rounded px-1.5 py-0.5">{item.spec.duration.toFixed(1)} s</span>}
        {isBusy(item) && ready && item.media?.version !== item.current && <span className="absolute left-2 top-2 text-[10px] bg-primary-600 text-white rounded px-1.5 py-0.5">{t('changeovers.updating')}</span>}
      </button>
      <div className="p-3 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <button type="button" onClick={() => onOpen(item)} className="font-medium text-gray-900 text-sm leading-snug text-left hover:text-primary-700">{item.name}</button>
          <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 group-hover/card:opacity-100">
            {item.status === 'failed' && <button type="button" onClick={() => onRetry(item)} title={t('changeovers.retry')} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><FiRefreshCw size={14} /></button>}
            {item.spec && <button type="button" onClick={() => onDuplicate(item)} title={t('changeovers.duplicate')} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><FiCopy size={14} /></button>}
            {!isBusy(item) && <button type="button" onClick={() => onRemove(item)} title={t('changeovers.remove')} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"><FiTrash2 size={14} /></button>}
          </div>
        </div>
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">{item.tags.map((tag) => <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{tag}</span>)}</div>
        )}
        <StatusLine item={item} />
      </div>
    </div>
  );
};

export const Changeovers = () => {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [create, setCreate] = useState(null);
  const openId = params.get('open');

  const load = useCallback(async () => {
    try {
      setItems(await changeoverService.list());
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const anyBusy = items.some(isBusy);
  useEffect(() => {
    if (!anyBusy) return undefined;
    const timer = setInterval(load, 2500);
    return () => clearInterval(timer);
  }, [anyBusy, load]);

  // Change overs waiting to be drawn are drawn here, on this computer's graphics card, one at a time.
  const waiting = items.filter((i) => i.status === 'drawing' && !i.drawingBy).map((i) => i.id).join(',');
  useEffect(() => {
    if (!waiting || !canDraw()) return;
    waiting.split(',').forEach((id) => drawChangeover(id).then((r) => r && load()));
  }, [waiting, load]);

  const open = (item) => setParams((p) => { const n = new URLSearchParams(p); n.set('open', item.id); return n; });
  const close = () => { setParams((p) => { const n = new URLSearchParams(p); n.delete('open'); return n; }); load(); };

  const allTags = useMemo(() => [...new Set(items.flatMap((i) => i.tags || []))].sort(), [items]);
  const shown = items.filter((i) => {
    if (tag && !(i.tags || []).includes(tag)) return false;
    const q = search.trim().toLowerCase();
    return !q || [i.name, i.prompt, ...(i.tags || [])].some((s) => String(s || '').toLowerCase().includes(q));
  });

  const remove = async (item) => {
    if (!window.confirm(t('changeovers.removeConfirm', { name: item.name }))) return;
    try {
      await changeoverService.remove(item.id);
      setItems((cur) => cur.filter((c) => c.id !== item.id));
    } catch (e) { showError(e); }
  };
  const retry = async (item) => {
    try { await changeoverService.retry(item.id); load(); } catch (e) { showError(e); }
  };
  const duplicate = async (item) => {
    try { const copy = await changeoverService.duplicate(item.id); setItems((cur) => [copy, ...cur]); toast.success(t('changeovers.duplicated')); } catch (e) { showError(e); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0"><FiShuffle size={20} /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('changeovers.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('changeovers.subtitle')}</p>
          </div>
        </div>
        <button type="button" onClick={() => setCreate({ prompt: '' })} className="btn btn-primary flex items-center gap-2"><FiPlus size={15} /> {t('changeovers.create.title')}</button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[200px] max-w-sm">
          <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('changeovers.search')} className="input w-full pl-9 text-sm" />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {['', ...allTags].map((tg) => (
            <button key={tg || 'all'} type="button" onClick={() => setTag(tg)} className={`text-xs rounded-full px-3 py-1 border ${tag === tg ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              {tg || t('changeovers.allTags')}
            </button>
          ))}
        </div>
        <span className="text-sm text-gray-500 ml-auto">{t('changeovers.count', { count: shown.length })}</span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('changeovers.loading')}</p>
      ) : shown.length === 0 ? (
        <div className="card text-center py-12 text-sm text-gray-500">{items.length ? t('changeovers.noMatch') : t('changeovers.empty')}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {shown.map((item) => <Card key={item.id} item={item} onOpen={open} onRemove={remove} onRetry={retry} onDuplicate={duplicate} />)}
        </div>
      )}

      {create && (
        <CreateDialog
          initialPrompt={create.prompt}
          onClose={() => setCreate(null)}
          onCreated={(made) => { setCreate(null); setItems((cur) => [made, ...cur]); open(made); }}
        />
      )}
      {openId && <ChangeoverStudio id={openId} onClose={close} onUsePrompt={(p) => { close(); setCreate({ prompt: p }); }} onChanged={load} />}
    </div>
  );
};
